import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { SearchOutlined } from '@ant-design/icons'
import { Col, Row, Space, Input, Image } from 'antd'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { ListReq } from 'Store/orders/types'
import { selectList, selectListEvent, selectNewOrder } from 'Store/orders/selector'
import { selectCurrentUser } from 'Store/user/selector'
import {
  numberWithComma,
  formatDateTime,
  formatPaymentChannel,
  formatShippingTypes,
  getOrderStatus,
  isFilter,
} from 'helpers/format'
import { StatusTag } from 'Components/utilities'
import { TableListPage } from 'Components/common/Table'
import { PrimaryButton } from 'Components/button'
import Detail from './CreateTax'
import { Pagination } from 'Components/Pagination'
import { orderListAPI } from 'Store/orders/api'
import { ImageFallback, OrderStatus, PaymentType } from 'helpers/constanst'
import Theme, { MyTheme } from 'Components/common/Theme'
import LinkText from 'Components/text/LinkText'
import { appConfig } from 'config'
import { cloneDeep } from 'lodash'
import { GetAllCustomerTaxReq } from 'Store/tax-management/types'
import { getAllCustomertaxListAPI } from 'Store/tax-management/api'

type Props = {} & ReduxProps

const TaxOrders: React.FunctionComponent<Props> = props => {
  const { currentUser, listEvent, dataList } = props
  const dispatch = useDispatch()
  const [drawer, setDrawer] = useState(false)
  const [resetFilter, setResetFilter] = useState(false)
  const [selectedOrder, setSelected] = useState(undefined as any)

  const initFilter = {
    keyword: '',
    payment_type: '',
    delivery_type: '',
    start_date: '',
    end_date: '',
  }

  const [filter, setFilter] = useState(cloneDeep(initFilter))
  const [pagination, setPagination] = useState({
    Page: 1,
    RowPerPage: 10,
  })
  const meta = dataList.meta
  const datas = dataList.data

  const onGetDataList = (): void => {
    const req: ListReq = {
      keyword: filter.keyword?.trim() || '',
      page: pagination.Page,
      per_page: pagination.RowPerPage,
      payment_type: `${PaymentType.POS},${PaymentType.TRANSFER_POS},${PaymentType.TRANSFER_QR},${PaymentType.TRANSFER_CREDIT_TERM}`,
      is_invoice: true,
    }
    dispatch(orderListAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onGetAllCustomerTax = (): void => {
    const req: GetAllCustomerTaxReq = {}
    dispatch(getAllCustomertaxListAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    if (currentUser?.accessToken) {
      onGetDataList()
    }
  }, [pagination])

  useEffect(() => {
    if (resetFilter) {
      pagination.Page = 1
      setPagination({ ...pagination })
      setResetFilter(false)
    }
  }, [resetFilter])

  const onClickSearch = () => {
    pagination.Page = 1
    setPagination({ ...pagination })
  }

  useEffect(() => {
    onGetAllCustomerTax()
    if (pagination.Page === 1) return
    pagination.Page = 1
    pagination.RowPerPage = 10
    setPagination({ ...pagination })
  }, [])

  const onFilter = (key: string, val: string) => {
    setFilter({
      ...filter,
      [key]: val,
    })
  }

  const onResetFilter = () => {
    setFilter(cloneDeep({ ...initFilter }))
    setResetFilter(true)
  }

  const onOpenDrawer = () => {
    setDrawer(true)
  }

  const onCloseDrawer = () => {
    setDrawer(false)
  }

  const onClickEdit = (order: any) => {
    setSelected(order)
    onOpenDrawer()
  }

  const onSuccess = () => {
    onCloseDrawer()
    onGetDataList()
  }

  const renderStatus = (status: string) => {
    const result = getOrderStatus(status)
    return <StatusTag color={result.color} text={result.text} />
  }

  const renderImage = (img: string) => (
    <ThumbImage src={appConfig.assetCdnEndpoint + img} fallback={ImageFallback} />
  )

  const renderProductDetail = (item: any) => {
    const isSameTitle = item.title == item.product_option_title
    return (
      <ProductItem>
        <Row align="middle" gutter={[32, 32]}>
          <Col style={{ width: '50%' }}>
            <Space align="start"></Space>
            <Row align="middle" gutter={[32, 32]}>
              <Col span={8}>{renderImage(item.photos)}</Col>
              <Col span={16}>
                <ProductName>{item.title}</ProductName>
                {!isSameTitle && <ProductDesc>{item.product_option_title}</ProductDesc>}
              </Col>
            </Row>
          </Col>
          <Col style={{ width: '25%' }}>{numberWithComma(item.quantity)}</Col>
          <Col style={{ width: '25%' }}>{numberWithComma(item.product_option_total_price, true)}</Col>
        </Row>
      </ProductItem>
    )
  }

  const renderDetail = (value: any, order: any, index: number) => {
    const orderNo = order?.order_no || ''
    return (
      <OrderItem key={index}>
        <OrderTitle>
          <Row justify="space-between" gutter={[32, 32]}>
            <Col>
              <Space size="small">เลขที่สั่งซื้อ : {orderNo}</Space>
            </Col>
            <Col>
              ชื่อผู้รับ : <MainText>{order?.receiver_name || ''}</MainText>
            </Col>
          </Row>
        </OrderTitle>
        <OrderDetail>
          <Row gutter={[16, 16]} align="middle">
            <Col style={{ width: '40%' }}>
              {order?.products?.map((item: any) => {
                return renderProductDetail(item)
              })}
            </Col>
            <Col style={{ width: '15%' }}>{formatPaymentChannel(order.payment_type) || '-'} </Col>
            <Col style={{ width: '15%' }}>{formatShippingTypes(order.shipping_type) || '-'} </Col>
            <Col style={{ width: '15%' }}>{renderStatus(order.status)}</Col>
            <Col style={{ width: '15%' }}>
              <Space direction="vertical">
                {formatDateTime(order.created_at)}
                {order.status !== OrderStatus.CANCEL && order.status !== OrderStatus.PENDING && (
                  <PrimaryButton text={'ออกใบกำกับภาษี'} size="middle" onClick={() => onClickEdit(order)} />
                )}
              </Space>
            </Col>
          </Row>
        </OrderDetail>
      </OrderItem>
    )
  }

  const renderEmpty = () => {
    const obj = {
      children: '',
      props: {} as any,
    }
    obj.props.rowSpan = 0
    obj.props.colSpan = 0
    return obj
  }

  const columns = [
    {
      title: 'รายการสินค้า',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '20%',
      render: (value: any, record: any, index: number) => {
        const obj = {
          children: renderDetail(value, record, index),
          props: {} as any,
        }
        obj.props.colSpan = 7
        return obj
      },
    },
    {
      title: 'จำนวน',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '10%',
      render: renderEmpty,
    },
    {
      title: 'ราคา (บาท)',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '10%',
      render: renderEmpty,
    },
    {
      title: 'ช่องทางจ่ายเงิน',
      dataIndex: 'payment_type',
      key: 'payment_type',
      width: '15%',
      render: renderEmpty,
    },
    {
      title: 'จัดส่งโดย',
      dataIndex: 'shipping_type',
      key: 'shipping_type',
      width: '15%',
      render: renderEmpty,
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: renderEmpty,
    },
    {
      title: 'วันที่สั่งซื้อ',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '15%',
      render: renderEmpty,
    },
  ]

  const DetailDrawer = (
    <Detail
      orderNo={selectedOrder?.order_no}
      status={selectedOrder?.status || ''}
      visible={drawer}
      onCloseDrawer={onCloseDrawer}
      onSuccess={onSuccess}
    />
  )

  const FilterContent = (
    <FilterWrapper>
      <Title>ค้นหาหมายเลขคำสั่งซื้อเพื่อออกใบกำกับภาษีเต็มรูปแบบ</Title>
      <Description>สามารถออกใบกำกับภาษีได้เฉพาะรายการสั่งซื้อผ่านเครื่อง Terminal</Description>
      <Row justify="center" align="bottom" gutter={[8, 1]}>
        <MyCol xs={24} md={12}>
          <SearchInput
            placeholder="ค้นหาด้วยหมายเลขคำสั่งซื้อ ชื่อลูกค้า หรือเบอร์โทรศัพท์ลูกค้า"
            prefix={<SearchOutlined />}
            allowClear
            value={filter.keyword}
            onChange={(evt: any): void => onFilter('keyword', evt.target.value)}
          />
        </MyCol>
        <MyCol>
          <Space size="middle">
            <PrimaryButton text="ค้นหา" btnType="Cancel" onClick={onClickSearch} />
            {isFilter(filter) && (
              <LinkText color={Theme.colors.primary} onClick={onResetFilter}>
                ล้างการค้นหา
              </LinkText>
            )}
          </Space>
        </MyCol>
      </Row>
    </FilterWrapper>
  )

  const PaginationContent = (
    <Pagination
      currentPage={pagination.Page}
      totalRows={meta?.totalCount || 0}
      perPage={pagination.RowPerPage}
      onClickPage={(page: number) => {
        pagination.Page = page
        setPagination({ ...pagination })
      }}
      onChangeNumber={(number: number) => {
        pagination.RowPerPage = number
        setPagination({ ...pagination })
      }}
    />
  )

  return (
    <>
      {FilterContent}
      <TableListPage
        columns={columns}
        datas={datas}
        isLoading={listEvent.IsLoading}
        paginationContent={PaginationContent}
        size="middle"
        offsetHeader={25}
      />
      {DetailDrawer}
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    dataList: selectList(state),
    listEvent: selectListEvent(state),
    newOrder: selectNewOrder(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

const FilterWrapper = styled.div`
  padding: 30px 10px;
  border: 1px solid #c4c4c4;
  box-sizing: border-box;
  border-radius: 10px;
  text-align: center;
`

const MyCol = styled(Col)`
  margin-top: 10px;
`

const SearchInput = styled(Input)`
  border-radius: 6px;
  height: 45px;
`

const OrderItem = styled.div`
  width: 100%;
  border-radius: 5px;
  margin-bottom: 10px;
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  background-color: #fafafa;
  border: 1px solid #f0f0f0;
`

const OrderTitle = styled.div`
  width: 100%;
  padding: 10px 15px;
`

const Title = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
`

const Description = styled.p`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const MainText = styled(LinkText)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  text-decoration: none;
  cursor: default;
`

const OrderDetail = styled.div`
  padding: 10px;
  //border: 1px solid #f0f0f0;
  background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.white};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  border-radius: 5px;
`

const ProductItem = styled.div`
  background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.white};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  &:last-child {
    border-bottom: none;
  }
`

const ThumbImage = styled(Image)`
  border-radius: 5px;
  width: 70px;
  height: 70px;
  object-fit: cover;
`

const ProductName = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const ProductDesc = styled(ProductName)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

export default connect(mapStateToProps)(TaxOrders)
